/*
 * Copyright (C) 2025-2026 The Klangmotor Authors (see AUTHORS.MD)
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

package io.peekandpoke.klang.script.parser

import io.peekandpoke.klang.common.SourceLocation
import io.peekandpoke.klang.script.ast.Argument
import io.peekandpoke.klang.script.ast.ArrayLiteral
import io.peekandpoke.klang.script.ast.ArrowFunction
import io.peekandpoke.klang.script.ast.ArrowFunctionBody
import io.peekandpoke.klang.script.ast.AssignmentExpression
import io.peekandpoke.klang.script.ast.BinaryOperation
import io.peekandpoke.klang.script.ast.BinaryOperator
import io.peekandpoke.klang.script.ast.BooleanLiteral
import io.peekandpoke.klang.script.ast.BreakStatement
import io.peekandpoke.klang.script.ast.CallExpression
import io.peekandpoke.klang.script.ast.ConstDeclaration
import io.peekandpoke.klang.script.ast.ContinueStatement
import io.peekandpoke.klang.script.ast.DoWhileStatement
import io.peekandpoke.klang.script.ast.ElseBranch
import io.peekandpoke.klang.script.ast.ExportDeclaration
import io.peekandpoke.klang.script.ast.ExportStatement
import io.peekandpoke.klang.script.ast.Expression
import io.peekandpoke.klang.script.ast.ExpressionStatement
import io.peekandpoke.klang.script.ast.ForStatement
import io.peekandpoke.klang.script.ast.Identifier
import io.peekandpoke.klang.script.ast.IfExpression
import io.peekandpoke.klang.script.ast.ImportStatement
import io.peekandpoke.klang.script.ast.IndexAccess
import io.peekandpoke.klang.script.ast.LetDeclaration
import io.peekandpoke.klang.script.ast.MemberAccess
import io.peekandpoke.klang.script.ast.NullLiteral
import io.peekandpoke.klang.script.ast.NumberLiteral
import io.peekandpoke.klang.script.ast.ObjectLiteral
import io.peekandpoke.klang.script.ast.Program
import io.peekandpoke.klang.script.ast.ReturnStatement
import io.peekandpoke.klang.script.ast.Statement
import io.peekandpoke.klang.script.ast.StringLiteral
import io.peekandpoke.klang.script.ast.TemplateLiteral
import io.peekandpoke.klang.script.ast.TemplatePart
import io.peekandpoke.klang.script.ast.TernaryExpression
import io.peekandpoke.klang.script.ast.UnaryOperation
import io.peekandpoke.klang.script.ast.UnaryOperator
import io.peekandpoke.klang.script.ast.WhileStatement
import io.peekandpoke.klang.script.runtime.KlangScriptSyntaxError

/**
 * Hand-rolled recursive descent parser for KlangScript
 *
 * This parser transforms source code text into an Abstract Syntax Tree (AST).
 * It replaces the previous better-parse combinator implementation to fix
 * Kotlin/JS production build issues.
 *
 * Capabilities:
 * - Numeric, string, boolean, and null literals
 * - Identifiers (variables and function names)
 * - Function calls with arguments (trailing commas allowed)
 * - Member access (dot notation): obj.property
 * - Method chaining: note("c").gain(0.5).pan("0 1")
 * - Arithmetic operators: +, -, *, /, %
 * - Comparison operators: ==, !=, <, <=, >, >=
 * - Logical operators: &&, ||
 * - Unary operators: -, +, !
 * - Arrow functions: x => x + 1, (a, b) => a + b, () => { ... }
 * - Object literals: { key: value }
 * - Array literals: [1, 2, 3]
 * - Variable declarations: let, const
 * - Import/export statements
 * - Return statements
 * - Block bodies for arrow functions
 * - Comments: // and /* */
 * - Source location tracking
 */
@Suppress("ConstPropertyName")
class KlangScriptParser private constructor(
    private val currentSource: String? = null,
) {
    companion object {
        // ── Char code constants for the tokenizer ────────────────────────────────
        // Named constants for readability. Kotlin inlines 'x'.code as a compile-time Int,
        // but named vals make the tokenizer's when-branches easier to scan.
        private const val C_SPACE = 32       // ' '.code
        private const val C_TAB = 9          // '\t'.code
        private const val C_CR = 13          // '\r'.code
        private const val C_LF = 10          // '\n'.code
        private const val C_SLASH = 47       // '/'.code
        private const val C_STAR = 42        // '*'.code
        private const val C_EQUALS = 61      // '='.code
        private const val C_BANG = 33        // '!'.code
        private const val C_LT = 60          // '<'.code
        private const val C_GT = 62          // '>'.code
        private const val C_AMP = 38         // '&'.code
        private const val C_PIPE = 124       // '|'.code
        private const val C_PLUS = 43        // '+'.code
        private const val C_MINUS = 45       // '-'.code
        private const val C_PERCENT = 37     // '%'.code
        private const val C_LPAREN = 40      // '('.code
        private const val C_RPAREN = 41      // ')'.code
        private const val C_LBRACE = 123     // '{'.code
        private const val C_RBRACE = 125     // '}'.code
        private const val C_LBRACKET = 91    // '['.code
        private const val C_RBRACKET = 93    // ']'.code
        private const val C_COMMA = 44       // ','.code
        private const val C_COLON = 58       // ':'.code
        private const val C_DOT = 46         // '.'.code
        private const val C_QUESTION = 63    // '?'.code
        private const val C_SEMICOLON = 59   // ';'.code
        private const val C_CARET = 94       // '^'.code
        private const val C_TILDE = 126      // '~'.code
        private const val C_DQUOTE = 34      // '"'.code
        private const val C_SQUOTE = 39      // '\''.code
        private const val C_BACKTICK = 96    // '`'.code
        private const val C_BACKSLASH = 92   // '\\'.code
        private const val C_UNDERSCORE = 95  // '_'.code
        private const val C_0 = 48           // '0'.code
        private const val C_1 = 49           // '1'.code  (binary digit)
        private const val C_7 = 55           // '7'.code  (octal max digit)
        private const val C_9 = 57           // '9'.code
        private const val C_A = 65           // 'A'.code
        private const val C_B = 66           // 'B'.code
        private const val C_E = 69           // 'E'.code
        private const val C_F = 70           // 'F'.code
        private const val C_O = 79           // 'O'.code
        private const val C_X = 88           // 'X'.code
        private const val C_a = 97           // 'a'.code
        private const val C_b = 98           // 'b'.code  (binary prefix)
        private const val C_e = 101          // 'e'.code  (scientific notation)
        private const val C_f = 102          // 'f'.code
        private const val C_n = 110          // 'n'.code  (escape: \n)
        private const val C_o = 111          // 'o'.code  (octal prefix)
        private const val C_r = 114          // 'r'.code  (escape: \r)
        private const val C_t = 116          // 't'.code  (escape: \t)
        private const val C_x = 120          // 'x'.code  (hex prefix)

        // ASCII-only identifier classification — avoids Char boxing on Kotlin/JS.
        // KlangScript intentionally supports ASCII identifiers only (a-z, A-Z, 0-9, _).
        private fun Int.isAsciiDigit(): Boolean = this in C_0..C_9
        private fun Int.isAsciiLetter(): Boolean = this in C_A..C_A + 25 || this in C_a..C_a + 25
        private fun Int.isAsciiLetterOrDigit(): Boolean = isAsciiLetter() || isAsciiDigit()

        /**
         * Is the `.` at [i] the decimal point of a number literal? Only when a digit (`2.5`) or an exponent
         * (`2.e5`, JS spelling) follows it; `2.pow` and `2.exp()` are member accesses on the number.
         */
        private fun IntArray.isFractionDotAt(i: Int): Boolean =
            this[i] == C_DOT && (i + 1 < size && this[i + 1].isAsciiDigit() || isExponentAt(i + 1))

        /** Does a complete exponent start at [i]: `e` or `E`, an optional sign, at least one digit? */
        private fun IntArray.isExponentAt(i: Int): Boolean {
            if (i >= size || (this[i] != C_e && this[i] != C_E)) return false

            val afterSign = if (i + 1 < size && (this[i + 1] == C_PLUS || this[i + 1] == C_MINUS)) i + 2 else i + 1

            return afterSign < size && this[afterSign].isAsciiDigit()
        }

        /**
         * Scans a decimal literal in [codes] from [from] and returns the index behind it: digits, at most one
         * fraction, an optional exponent. The dot is taken only when a digit or an exponent follows it, and
         * only once: `2.5` is one number, `2.pow` is `2` `.` `pow`, `1.2.3` is `1.2` `.` `3` (which the parser
         * then rejects), and a trailing `2.` is `2` followed by a stray dot. Before the lookahead the scanner
         * ate every dot and no method was callable on a number literal
         * (KLANG-00193 (klangscript number methods)).
         *
         * A companion function on purpose: a local function inside `tokenize` that writes the lexer index
         * would make Kotlin box that index for the whole lexer loop.
         */
        private fun scanDecimalEnd(codes: IntArray, from: Int): Int {
            var i = from

            while (i < codes.size && codes[i].isAsciiDigit()) {
                i++
            }
            if (i < codes.size && codes.isFractionDotAt(i)) {
                i++
                while (i < codes.size && codes[i].isAsciiDigit()) {
                    i++
                }
            }
            // Scientific notation: e/E, optional +/-, at least one digit. A bare `2e` or `2e+` is the number
            // `2` followed by an identifier, which the parser reports; before, the token `2e` reached
            // `toDouble()` and threw a NumberFormatException with no source location.
            if (codes.isExponentAt(i)) {
                i++
                if (codes[i] == C_PLUS || codes[i] == C_MINUS) {
                    i++
                }
                while (i < codes.size && codes[i].isAsciiDigit()) {
                    i++
                }
            }

            return i
        }

        /** Keyword → TokenType lookup map. */
        private val keywords = mapOf(
            "true" to TokenType.TRUE,
            "false" to TokenType.FALSE,
            "null" to TokenType.NULL,
            "let" to TokenType.LET,
            "const" to TokenType.CONST,
            "import" to TokenType.IMPORT,
            "export" to TokenType.EXPORT,
            "return" to TokenType.RETURN,
            "in" to TokenType.IN,
            "if" to TokenType.IF,
            "else" to TokenType.ELSE,
            "while" to TokenType.WHILE,
            "do" to TokenType.DO,
            "for" to TokenType.FOR,
            "break" to TokenType.BREAK,
            "continue" to TokenType.CONTINUE,
        )

        /**
         * Parse KlangScript source code into an AST
         *
         * @param source The source code to parse
         * @param sourceName Optional source file name for error reporting
         * @return Program AST node
         * @throws KlangScriptSyntaxError if the source contains syntax errors
         */
        fun parse(source: String, sourceName: String? = null): Program {
            val parser = KlangScriptParser(currentSource = sourceName)
            return parser.parseInternal(source)
        }
    }

    // ============================================================
    // Token Types and Lexer
    // ============================================================

    /** Token types produced by the lexer. */
    private enum class TokenType {
        // Literals
        NUMBER, STRING, BACKTICK_STRING, IDENTIFIER,

        // Keywords (must match before IDENTIFIER)
        TRUE, FALSE, NULL, LET, CONST, IMPORT, EXPORT, RETURN, IN,
        IF, ELSE, WHILE, DO, FOR, BREAK, CONTINUE,

        // Punctuation
        LEFT_PAREN, RIGHT_PAREN, LEFT_BRACE, RIGHT_BRACE,
        LEFT_BRACKET, RIGHT_BRACKET, COMMA, COLON, DOT, QUESTION, SEMICOLON,

        // Multi-char operators (MUST tokenize before single-char — longer before shorter)
        ARROW,               // =>
        TRIPLE_EQUALS,       // ===  (before ==)
        DOUBLE_EQUALS,       // ==   (before =)
        NOT_DOUBLE_EQUALS,   // !==  (before !=)
        NOT_EQUALS,          // !=   (before !)
        SHIFT_LEFT_EQUALS,   // <<=  (before << and <=)
        SHIFT_LEFT,          // <<   (before <= and <)
        LESS_EQUAL,          // <=   (before <)
        UNSIGNED_SHIFT_RIGHT, // >>>  (before >>= and >>)
        SHIFT_RIGHT_EQUALS,  // >>=  (before >> and >=)
        SHIFT_RIGHT,         // >>   (before >= and >)
        GREATER_EQUAL,       // >=   (before >)
        DOUBLE_AMP,          // &&   (before &= and &)
        AMP_EQUALS,          // &=   (before &)
        DOUBLE_PIPE,         // ||   (before |= and |)
        PIPE_EQUALS,         // |=   (before |)
        DOUBLE_QUESTION,     // ??   (before ?. and ?)
        QUESTION_DOT,        // ?.   (before ?)
        CARET_EQUALS,        // ^=   (before ^)
        DOUBLE_STAR_EQUALS,  // **=  (before ** and *=)
        DOUBLE_STAR,         // **   (before *=)
        PLUS_PLUS,           // ++   (before + and +=)
        MINUS_MINUS,         // --   (before - and -=)
        PLUS_EQUALS,         // +=   (before +)
        MINUS_EQUALS,        // -=   (before -)
        STAR_EQUALS,         // *=   (before *)
        SLASH_EQUALS,        // /=   (before /)
        PERCENT_EQUALS,      // %=   (before %)

        // Single-char operators
        EQUALS, PLUS, MINUS, STAR, SLASH, PERCENT,
        EXCLAMATION, LESS_THAN, GREATER_THAN,
        AMP, PIPE, CARET, TILDE,

        EOF
    }

    /** A lexed token with type, text content, and source position. */
    private data class Token(
        val type: TokenType,
        val text: String,
        val line: Int,      // 1-based (start line)
        val column: Int,    // 1-based (start column)
        val endLine: Int,   // 1-based
        val endColumn: Int,  // 1-based, exclusive
    )

    private lateinit var tokens: List<Token>
    private var pos: Int = 0

    private fun parseInternal(source: String): Program {
        this.tokens = tokenize(source)
        this.pos = 0
        return parseProgram()
    }

    /**
     * Tokenize source code into a list of tokens
     */
    private fun tokenize(source: String): List<Token> {
        val tokens = mutableListOf<Token>()

        // Convert source string to IntArray of char codes once. On Kotlin/JS, String[i] returns
        // a boxed Char (heap-allocated wrapper). IntArray[i] returns a plain JS number — no boxing.
        // This is critical for a lexer that runs on every keystroke in a live-coding environment.
        val codes = IntArray(source.length) { source[it].code }

        var i = 0
        var line = 1
        var column = 1

        fun addToken(type: TokenType, text: String, startLine: Int, startColumn: Int, endLine: Int, endColumn: Int) {
            tokens.add(
                Token(
                    type = type,
                    text = text,
                    line = startLine,
                    column = startColumn,
                    endLine = endLine,
                    endColumn = endColumn
                )
            )
        }

        // Overload for non-multiline tokens (single line)
        fun addToken(type: TokenType, text: String, startColumn: Int) {
            addToken(type, text, line, startColumn, line, column)
        }

        while (i < source.length) {
            val ch = codes[i]
            val startColumn = column

            when {
                // Whitespace
                ch == C_SPACE || ch == C_TAB || ch == C_CR -> {
                    i++
                    column++
                }

                // Newline
                ch == C_LF -> {
                    i++
                    line++
                    column = 1
                }

                // Single-line comment
                ch == C_SLASH && i + 1 < source.length && codes[i + 1] == C_SLASH -> {
                    i += 2
                    column += 2
                    while (i < source.length && codes[i] != C_LF) {
                        i++
                        column++
                    }
                }

                // Multi-line comment
                ch == C_SLASH && i + 1 < source.length && codes[i + 1] == C_STAR -> {
                    val startLine = line
                    var closed = false
                    i += 2
                    column += 2

                    while (i < source.length) {
                        if (codes[i] == C_STAR && i + 1 < source.length && codes[i + 1] == C_SLASH) {
                            i += 2
                            column += 2
                            closed = true
                            break
                        }

                        if (codes[i] == C_LF) {
                            line++
                            column = 1
                        } else {
                            column++
                        }
                        i++
                    }

                    if (!closed) {
                        throw KlangScriptSyntaxError(
                            "Unterminated block comment",
                            location = SourceLocation(currentSource, startLine, startColumn, line, column),
                        )
                    }
                }

                // Multi-char operators (MUST check before single-char — longer before shorter)

                // === (before == and =)
                ch == C_EQUALS && i + 2 < source.length && codes[i + 1] == C_EQUALS && codes[i + 2] == C_EQUALS -> {
                    addToken(TokenType.TRIPLE_EQUALS, "===", startColumn)
                    i += 3
                    column += 3
                }

                // => (before ==)
                ch == C_EQUALS && i + 1 < source.length && codes[i + 1] == C_GT -> {
                    addToken(TokenType.ARROW, "=>", startColumn)
                    i += 2
                    column += 2
                }

                // == (before =)
                ch == C_EQUALS && i + 1 < source.length && codes[i + 1] == C_EQUALS -> {
                    addToken(TokenType.DOUBLE_EQUALS, "==", startColumn)
                    i += 2
                    column += 2
                }

                // !== (before != and !)
                ch == C_BANG && i + 2 < source.length && codes[i + 1] == C_EQUALS && codes[i + 2] == C_EQUALS -> {
                    addToken(TokenType.NOT_DOUBLE_EQUALS, "!==", startColumn)
                    i += 3
                    column += 3
                }

                // != (before !)
                ch == C_BANG && i + 1 < source.length && codes[i + 1] == C_EQUALS -> {
                    addToken(TokenType.NOT_EQUALS, "!=", startColumn)
                    i += 2
                    column += 2
                }

                // <<= (before << and <=)
                ch == C_LT && i + 2 < source.length && codes[i + 1] == C_LT && codes[i + 2] == C_EQUALS -> {
                    addToken(TokenType.SHIFT_LEFT_EQUALS, "<<=", startColumn)
                    i += 3
                    column += 3
                }

                // << (before <=)
                ch == C_LT && i + 1 < source.length && codes[i + 1] == C_LT -> {
                    addToken(TokenType.SHIFT_LEFT, "<<", startColumn)
                    i += 2
                    column += 2
                }

                ch == C_LT && i + 1 < source.length && codes[i + 1] == C_EQUALS -> {
                    addToken(TokenType.LESS_EQUAL, "<=", startColumn)
                    i += 2
                    column += 2
                }

                // >>> (before >>= and >>)
                ch == C_GT && i + 2 < source.length && codes[i + 1] == C_GT && codes[i + 2] == C_GT -> {
                    addToken(TokenType.UNSIGNED_SHIFT_RIGHT, ">>>", startColumn)
                    i += 3
                    column += 3
                }

                // >>= (before >> and >=)
                ch == C_GT && i + 2 < source.length && codes[i + 1] == C_GT && codes[i + 2] == C_EQUALS -> {
                    addToken(TokenType.SHIFT_RIGHT_EQUALS, ">>=", startColumn)
                    i += 3
                    column += 3
                }

                // >> (before >=)
                ch == C_GT && i + 1 < source.length && codes[i + 1] == C_GT -> {
                    addToken(TokenType.SHIFT_RIGHT, ">>", startColumn)
                    i += 2
                    column += 2
                }

                ch == C_GT && i + 1 < source.length && codes[i + 1] == C_EQUALS -> {
                    addToken(TokenType.GREATER_EQUAL, ">=", startColumn)
                    i += 2
                    column += 2
                }

                ch == C_AMP && i + 1 < source.length && codes[i + 1] == C_AMP -> {
                    addToken(TokenType.DOUBLE_AMP, "&&", startColumn)
                    i += 2
                    column += 2
                }

                // &= (before &)
                ch == C_AMP && i + 1 < source.length && codes[i + 1] == C_EQUALS -> {
                    addToken(TokenType.AMP_EQUALS, "&=", startColumn)
                    i += 2
                    column += 2
                }

                ch == C_PIPE && i + 1 < source.length && codes[i + 1] == C_PIPE -> {
                    addToken(TokenType.DOUBLE_PIPE, "||", startColumn)
                    i += 2
                    column += 2
                }

                // |= (before |)
                ch == C_PIPE && i + 1 < source.length && codes[i + 1] == C_EQUALS -> {
                    addToken(TokenType.PIPE_EQUALS, "|=", startColumn)
                    i += 2
                    column += 2
                }

                // ++ (before + and +=)
                ch == C_PLUS && i + 1 < source.length && codes[i + 1] == C_PLUS -> {
                    addToken(TokenType.PLUS_PLUS, "++", startColumn)
                    i += 2
                    column += 2
                }

                // += (before +)
                ch == C_PLUS && i + 1 < source.length && codes[i + 1] == C_EQUALS -> {
                    addToken(TokenType.PLUS_EQUALS, "+=", startColumn)
                    i += 2
                    column += 2
                }

                // -- (before - and -=)
                ch == C_MINUS && i + 1 < source.length && codes[i + 1] == C_MINUS -> {
                    addToken(TokenType.MINUS_MINUS, "--", startColumn)
                    i += 2
                    column += 2
                }

                // -= (before -)
                ch == C_MINUS && i + 1 < source.length && codes[i + 1] == C_EQUALS -> {
                    addToken(TokenType.MINUS_EQUALS, "-=", startColumn)
                    i += 2
                    column += 2
                }

                // **= (before ** and *=)
                ch == C_STAR && i + 2 < source.length && codes[i + 1] == C_STAR && codes[i + 2] == C_EQUALS -> {
                    addToken(TokenType.DOUBLE_STAR_EQUALS, "**=", startColumn)
                    i += 3
                    column += 3
                }

                // ** (before *=)
                ch == C_STAR && i + 1 < source.length && codes[i + 1] == C_STAR -> {
                    addToken(TokenType.DOUBLE_STAR, "**", startColumn)
                    i += 2
                    column += 2
                }

                // *= (before *)
                ch == C_STAR && i + 1 < source.length && codes[i + 1] == C_EQUALS -> {
                    addToken(TokenType.STAR_EQUALS, "*=", startColumn)
                    i += 2
                    column += 2
                }

                // /= (before /)
                ch == C_SLASH && i + 1 < source.length && codes[i + 1] == C_EQUALS -> {
                    addToken(TokenType.SLASH_EQUALS, "/=", startColumn)
                    i += 2
                    column += 2
                }

                // %= (before %)
                ch == C_PERCENT && i + 1 < source.length && codes[i + 1] == C_EQUALS -> {
                    addToken(TokenType.PERCENT_EQUALS, "%=", startColumn)
                    i += 2
                    column += 2
                }

                // Single-char tokens
                ch == C_LPAREN -> {
                    addToken(TokenType.LEFT_PAREN, "(", startColumn)
                    i++
                    column++
                }

                ch == C_RPAREN -> {
                    addToken(TokenType.RIGHT_PAREN, ")", startColumn)
                    i++
                    column++
                }

                ch == C_LBRACE -> {
                    addToken(TokenType.LEFT_BRACE, "{", startColumn)
                    i++
                    column++
                }

                ch == C_RBRACE -> {
                    addToken(TokenType.RIGHT_BRACE, "}", startColumn)
                    i++
                    column++
                }

                ch == C_LBRACKET -> {
                    addToken(TokenType.LEFT_BRACKET, "[", startColumn)
                    i++
                    column++
                }

                ch == C_RBRACKET -> {
                    addToken(TokenType.RIGHT_BRACKET, "]", startColumn)
                    i++
                    column++
                }

                ch == C_COMMA -> {
                    addToken(TokenType.COMMA, ",", startColumn)
                    i++
                    column++
                }

                ch == C_COLON -> {
                    addToken(TokenType.COLON, ":", startColumn)
                    i++
                    column++
                }

                ch == C_DOT -> {
                    addToken(TokenType.DOT, ".", startColumn)
                    i++
                    column++
                }

                // ?? (before ?. and ?)
                ch == C_QUESTION && i + 1 < source.length && codes[i + 1] == C_QUESTION -> {
                    addToken(TokenType.DOUBLE_QUESTION, "??", startColumn)
                    i += 2
                    column += 2
                }

                // ?. (before ?)
                ch == C_QUESTION && i + 1 < source.length && codes[i + 1] == C_DOT -> {
                    addToken(TokenType.QUESTION_DOT, "?.", startColumn)
                    i += 2
                    column += 2
                }

                ch == C_QUESTION -> {
                    addToken(TokenType.QUESTION, "?", startColumn)
                    i++
                    column++
                }

                ch == C_SEMICOLON -> {
                    addToken(TokenType.SEMICOLON, ";", startColumn)
                    i++
                    column++
                }

                ch == C_EQUALS -> {
                    addToken(TokenType.EQUALS, "=", startColumn)
                    i++
                    column++
                }

                ch == C_PLUS -> {
                    addToken(TokenType.PLUS, "+", startColumn)
                    i++
                    column++
                }

                ch == C_MINUS -> {
                    addToken(TokenType.MINUS, "-", startColumn)
                    i++
                    column++
                }

                ch == C_STAR -> {
                    addToken(TokenType.STAR, "*", startColumn)
                    i++
                    column++
                }

                ch == C_SLASH -> {
                    addToken(TokenType.SLASH, "/", startColumn)
                    i++
                    column++
                }

                ch == C_PERCENT -> {
                    addToken(TokenType.PERCENT, "%", startColumn)
                    i++
                    column++
                }

                ch == C_BANG -> {
                    addToken(TokenType.EXCLAMATION, "!", startColumn)
                    i++
                    column++
                }

                ch == C_LT -> {
                    addToken(TokenType.LESS_THAN, "<", startColumn)
                    i++
                    column++
                }

                ch == C_GT -> {
                    addToken(TokenType.GREATER_THAN, ">", startColumn)
                    i++
                    column++
                }

                // ^= (before ^)
                ch == C_CARET && i + 1 < source.length && codes[i + 1] == C_EQUALS -> {
                    addToken(TokenType.CARET_EQUALS, "^=", startColumn)
                    i += 2
                    column += 2
                }

                ch == C_AMP -> {
                    addToken(TokenType.AMP, "&", startColumn)
                    i++
                    column++
                }

                ch == C_PIPE -> {
                    addToken(TokenType.PIPE, "|", startColumn)
                    i++
                    column++
                }

                ch == C_CARET -> {
                    addToken(TokenType.CARET, "^", startColumn)
                    i++
                    column++
                }

                ch == C_TILDE -> {
                    addToken(TokenType.TILDE, "~", startColumn)
                    i++
                    column++
                }

                // String literals
                ch == C_DQUOTE || ch == C_SQUOTE -> {
                    val startLine = line  // Save starting line for multiline strings
                    val quote = ch
                    val sb = StringBuilder()
                    i++ // Skip opening quote
                    column++

                    while (i < source.length && codes[i] != quote) {
                        if (codes[i] == C_BACKSLASH && i + 1 < source.length) {
                            // Escape sequence
                            i++
                            column++
                            when (codes[i]) {
                                C_n -> sb.append('\n')
                                C_t -> sb.append('\t')
                                C_r -> sb.append('\r')
                                C_BACKSLASH -> sb.append('\\')
                                C_DQUOTE -> sb.append('"')
                                C_SQUOTE -> sb.append('\'')
                                else -> sb.append(codes[i].toChar())
                            }
                        } else {
                            sb.append(codes[i].toChar())
                        }
                        if (codes[i] == C_LF) {
                            line++
                            column = 1
                        } else {
                            column++
                        }
                        i++
                    }

                    if (i >= source.length) {
                        throw KlangScriptSyntaxError(
                            "Unterminated string",
                            location = SourceLocation(currentSource, startLine, startColumn, line, column),
                        )
                    }

                    i++ // Skip closing quote
                    column++
                    addToken(TokenType.STRING, sb.toString(), startLine, startColumn, line, column)
                }

                // Backtick strings
                ch == C_BACKTICK -> {
                    val startLine = line  // Save starting line for multiline strings
                    val sb = StringBuilder()
                    i++ // Skip opening backtick
                    column++

                    while (i < source.length && codes[i] != C_BACKTICK) {
                        if (codes[i] == C_BACKSLASH && i + 1 < source.length) {
                            // Escape sequence
                            i++
                            column++
                            when (codes[i]) {
                                C_n -> sb.append('\n')
                                C_t -> sb.append('\t')
                                C_r -> sb.append('\r')
                                C_BACKSLASH -> sb.append('\\')
                                C_BACKTICK -> sb.append('`')
                                else -> sb.append(codes[i].toChar())
                            }
                        } else {
                            sb.append(codes[i].toChar())
                        }
                        if (codes[i] == C_LF) {
                            line++
                            column = 1
                        } else {
                            column++
                        }
                        i++
                    }

                    if (i >= source.length) {
                        throw KlangScriptSyntaxError(
                            "Unterminated backtick string",
                            location = SourceLocation(currentSource, startLine, startColumn, line, column),
                        )
                    }

                    i++ // Skip closing backtick
                    column++
                    addToken(TokenType.BACKTICK_STRING, sb.toString(), startLine, startColumn, line, column)
                }

                // Numbers (including hex 0x, octal 0o, binary 0b, scientific notation: 1e6, 2.5e-3, 1E+10)
                ch.isAsciiDigit() -> {
                    val start = i

                    // Check for hex, octal, or binary prefix
                    if (ch == C_0 && i + 1 < source.length) {
                        val prefix = codes[i + 1]
                        when {
                            // Hex: 0x or 0X
                            prefix == C_x || prefix == C_X -> {
                                i += 2
                                column += 2
                                val hexStart = i
                                while (i < source.length &&
                                    (codes[i].isAsciiDigit() || codes[i] in C_a..C_f || codes[i] in C_A..C_F)
                                ) {
                                    i++
                                    column++
                                }
                                val hexStr = source.substring(hexStart, i)
                                val numValue = hexStr.toLong(16).toDouble()
                                addToken(TokenType.NUMBER, numValue.toString(), startColumn)
                            }
                            // Octal: 0o or 0O
                            prefix == C_o || prefix == C_O -> {
                                i += 2
                                column += 2
                                val octStart = i
                                while (i < source.length && codes[i] in C_0..C_7) {
                                    i++
                                    column++
                                }
                                val octStr = source.substring(octStart, i)
                                val numValue = octStr.toLong(8).toDouble()
                                addToken(TokenType.NUMBER, numValue.toString(), startColumn)
                            }
                            // Binary: 0b or 0B
                            prefix == C_b || prefix == C_B -> {
                                i += 2
                                column += 2
                                val binStart = i
                                while (i < source.length && (codes[i] == C_0 || codes[i] == C_1)) {
                                    i++
                                    column++
                                }
                                val binStr = source.substring(binStart, i)
                                val numValue = binStr.toLong(2).toDouble()
                                addToken(TokenType.NUMBER, numValue.toString(), startColumn)
                            }
                            // Regular number starting with 0
                            else -> {
                                // A decimal literal never spans a line, so the column moves with the index
                                val end = scanDecimalEnd(codes, i)
                                column += end - i
                                i = end
                                addToken(TokenType.NUMBER, source.substring(start, i), startColumn)
                            }
                        }
                    } else {
                        // A decimal literal never spans a line, so the column moves with the index
                        val end = scanDecimalEnd(codes, i)
                        column += end - i
                        i = end
                        addToken(TokenType.NUMBER, source.substring(start, i), startColumn)
                    }
                }

                // Identifiers and keywords
                ch.isAsciiLetter() || ch == C_UNDERSCORE -> {
                    val start = i
                    while (i < source.length && (codes[i].isAsciiLetterOrDigit() || codes[i] == C_UNDERSCORE)) {
                        i++
                        column++
                    }
                    val text = source.substring(start, i)
                    val type = keywords[text] ?: TokenType.IDENTIFIER
                    addToken(type, text, startColumn)
                }

                else -> {
                    throw KlangScriptSyntaxError(
                        "Unexpected character: ${ch.toChar()}",
                        location = SourceLocation(currentSource, line, column, line, column + 1),
                    )
                }
            }
        }

        // Add EOF token
        tokens.add(Token(TokenType.EOF, "", line, column, line, column))
        return tokens
    }

    // ============================================================
    // Parser Helper Methods
    // ============================================================

    private fun peek(): Token = tokens[pos]

    private fun previous(): Token = tokens[pos - 1]

    private fun isAtEnd(): Boolean = peek().type == TokenType.EOF

    private fun advance(): Token = tokens[pos++]

    private fun check(type: TokenType): Boolean = !isAtEnd() && peek().type == type

    /** Lookahead: is the token at [pos + offset] of [type]? */
    private fun checkAt(offset: Int, type: TokenType): Boolean {
        val idx = pos + offset
        if (idx >= tokens.size) return false
        val tok = tokens[idx]
        return tok.type != TokenType.EOF && tok.type == type
    }

    private fun match(vararg types: TokenType): Boolean {
        for (type in types) {
            if (check(type)) {
                advance()
                return true
            }
        }
        return false
    }

    private fun consume(type: TokenType, message: String): Token {
        if (check(type)) return advance()
        error(message)
    }

    /**
     * Consumes the `from` of an import. `from` and `as` are contextual keywords, as in JavaScript: the lexer emits
     * them as IDENTIFIERs, so they stay ordinary names everywhere else (`range(from = 1, to = 2)`,
     * `(from, to) => ...`, `x.as`), and only the import and export grammar reads them as keywords.
     */
    private fun consumeFrom(message: String): Token {
        if (checkContextualKeyword("from")) {
            return advance()
        }

        error(message)
    }

    /** Consumes the `as` of an import or export alias if it is next, see [consumeFrom]. */
    private fun matchAs(): Boolean {
        if (!checkContextualKeyword("as")) {
            return false
        }

        advance()

        return true
    }

    private fun checkContextualKeyword(text: String): Boolean = check(TokenType.IDENTIFIER) && peek().text == text

    private fun error(message: String): Nothing {
        val token = if (isAtEnd()) previous() else peek()
        throw KlangScriptSyntaxError(
            message,
            location = SourceLocation(currentSource, token.line, token.column, token.endLine, token.endColumn),
        )
    }

    /**
     * Skip any semicolons (used as optional statement terminators outside for-loop headers).
     *
     * @return true if at least one semicolon was consumed.
     */
    private fun skipSemicolons(): Boolean {
        var consumed = false

        while (check(TokenType.SEMICOLON)) {
            advance()
            consumed = true
        }

        return consumed
    }

    /**
     * Rejects a statement that begins on the line the previous one ended on without a `;` between them.
     *
     * Newlines are not tokens here and semicolons are optional separators, so two juxtaposed expressions are
     * simply two statements: `velocity("<1.0 0.85>*4")tag("hats")` (a missing dot, found in Der Schmetterling)
     * parses as a finished chain plus an orphan `tag(...)` mapper whose value nothing consumes. It compiles, it
     * renders, it sounds identical, and the tag is silently gone. Almost every pattern method has a bare mapper
     * overload for `apply`/`superimpose`, so a dropped dot nearly always still resolves.
     * See KLANG-00194 (klangscript statement boundaries).
     *
     * The rule is deliberately narrow: only a SHARED LINE is an error. Statements on their own lines stay legal,
     * so no multi-line chain and no leading-dot continuation line can trip over it.
     */
    private fun requireStatementBoundary() {
        val next = peek()

        if (next.line != previous().endLine) {
            return
        }

        // A closer or a separator can never begin a statement, so a shared line says nothing about it:
        // `stack(\n  s("bd")\n)).gain(1)` is a stray bracket, not a missing boundary. Let parseStatement()
        // report what it actually is, instead of proposing a newline that would not help.
        val canBeginStatement = when (next.type) {
            TokenType.RIGHT_PAREN, TokenType.RIGHT_BRACE, TokenType.RIGHT_BRACKET,
            TokenType.COMMA, TokenType.COLON,
            -> false

            else -> true
        }

        if (!canBeginStatement) {
            return
        }

        // An identifier that is immediately called is the missing-dot shape
        if (next.type == TokenType.IDENTIFIER && checkAt(1, TokenType.LEFT_PAREN)) {
            // Deliberately NOT offering "a newline" here: a newline separates these two statements
            // just fine, and then the orphan is silently dropped again exactly as before, because
            // phase 2 (newline sensitivity) is not implemented. The dot is the fix; `;` is the escape
            // hatch for someone who really did mean two statements.
            error("Did you mean '.${next.text}(...)'? Otherwise put ';' between the two statements.")
        }

        error("Expected a newline or ';' between statements.")
    }

    private fun Token.toSourceLocation(): SourceLocation {
        return SourceLocation(
            source = currentSource,
            startLine = line,
            startColumn = column,
            endLine = endLine,
            endColumn = endColumn
        )
    }

    // ============================================================
    // Expression Parsing
    // ============================================================

    private fun parseExpression(): Expression {
        return parseArrowFunction()
    }

    /**
     * Arrow functions (lowest precedence, right-associative)
     * Syntax: x => expr, (a, b) => expr, () => { ... }
     */
    private fun parseArrowFunction(): Expression {
        // Save position for potential backtracking
        val checkpoint = pos

        // Try to parse arrow parameters
        val maybeParams = tryParseArrowParams()

        if (maybeParams != null && match(TokenType.ARROW)) {
            // Confirmed arrow function
            val arrowToken = previous()
            val body = parseArrowBody()
            return ArrowFunction(maybeParams, body, arrowToken.toSourceLocation())
        }

        // Not an arrow function - backtrack
        pos = checkpoint
        return parseAssignment()
    }

    /**
     * Assignment expressions (=, +=, -=, *=, /=, %=)
     * Right-associative, lower precedence than ternary.
     * Compound assignments are desugared to AssignmentExpression(target, BinaryOperation(target, op, value))
     */
    private fun parseAssignment(): Expression {
        val left = parseTernary()

        // Check for assignment operators
        val assignOp = when {
            check(TokenType.EQUALS) -> null  // plain =
            check(TokenType.PLUS_EQUALS) -> BinaryOperator.ADD
            check(TokenType.MINUS_EQUALS) -> BinaryOperator.SUBTRACT
            check(TokenType.STAR_EQUALS) -> BinaryOperator.MULTIPLY
            check(TokenType.SLASH_EQUALS) -> BinaryOperator.DIVIDE
            check(TokenType.PERCENT_EQUALS) -> BinaryOperator.MODULO
            check(TokenType.AMP_EQUALS) -> BinaryOperator.BITWISE_AND
            check(TokenType.PIPE_EQUALS) -> BinaryOperator.BITWISE_OR
            check(TokenType.CARET_EQUALS) -> BinaryOperator.BITWISE_XOR
            check(TokenType.SHIFT_LEFT_EQUALS) -> BinaryOperator.SHIFT_LEFT
            check(TokenType.SHIFT_RIGHT_EQUALS) -> BinaryOperator.SHIFT_RIGHT
            check(TokenType.DOUBLE_STAR_EQUALS) -> BinaryOperator.POWER
            else -> return left  // Not an assignment
        }

        // Verify target is assignable
        if (left !is Identifier && left !is MemberAccess && left !is IndexAccess) {
            error("Invalid assignment target")
        }

        val opToken = advance()  // consume the assignment operator
        val right = parseAssignment()  // right-associative

        val value = if (assignOp != null) {
            // Desugar compound assignment: x += y  →  x = x + y
            BinaryOperation(left, assignOp, right, opToken.toSourceLocation())
        } else {
            right
        }

        return AssignmentExpression(left, value, opToken.toSourceLocation())
    }

    /**
     * Ternary operator: condition ? thenExpr : elseExpr
     * Right-associative.
     */
    private fun parseTernary(): Expression {
        val condition = parseNullishCoalescing()

        if (!match(TokenType.QUESTION)) return condition

        val questionToken = previous()
        val thenExpr = parseTernary()  // Right-associative
        consume(TokenType.COLON, "Expected ':' in ternary expression")
        val elseExpr = parseTernary()  // Right-associative

        return TernaryExpression(condition, thenExpr, elseExpr, questionToken.toSourceLocation())
    }

    /**
     * Try to parse arrow function parameters
     * Returns null if doesn't match arrow parameter pattern
     */
    private fun tryParseArrowParams(): List<String>? {
        return when {
            // Single param: x =>
            check(TokenType.IDENTIFIER) -> {
                val lookahead = tokens.getOrNull(pos + 1)
                if (lookahead?.type == TokenType.ARROW) {
                    listOf(advance().text)
                } else {
                    null
                }
            }

            // Multiple/zero params: (a, b) => or () =>
            check(TokenType.LEFT_PAREN) -> {
                val saved = pos
                advance() // consume (

                val params = mutableListOf<String>()
                while (!check(TokenType.RIGHT_PAREN) && !isAtEnd()) {
                    if (!check(TokenType.IDENTIFIER)) {
                        pos = saved
                        return null
                    }
                    params.add(advance().text)
                    if (!match(TokenType.COMMA)) break
                }

                if (!check(TokenType.RIGHT_PAREN)) {
                    pos = saved
                    return null
                }
                advance() // consume )

                // Must be followed by arrow
                if (!check(TokenType.ARROW)) {
                    pos = saved
                    return null
                }

                params
            }

            else -> null
        }
    }

    /**
     * Parse arrow function body
     * Can be expression body or block body
     *
     * Disambiguates between object literals and block bodies:
     * - `=> { key: value }` is an object literal (expression body)
     * - `=> { statement; }` is a block body
     */
    private fun parseArrowBody(): ArrowFunctionBody {
        return when {
            // Check if { is followed by identifier : (object literal)
            check(TokenType.LEFT_BRACE) && isObjectLiteralStart() -> {
                // Object literal as expression body
                val expr = parseArrowFunction() // Right-associative
                ArrowFunctionBody.ExpressionBody(expr)
            }

            // Block body: { statements }
            match(TokenType.LEFT_BRACE) -> {
                val statements = parseBlockStatements()
                consume(TokenType.RIGHT_BRACE, "Expected '}' after block")
                ArrowFunctionBody.BlockBody(statements)
            }

            // Expression body: expr (implicit return)
            else -> {
                val expr = parseArrowFunction() // Right-associative
                ArrowFunctionBody.ExpressionBody(expr)
            }
        }
    }

    /**
     * Check if current position is start of an object literal
     * Look ahead for pattern: { identifier : OR { identifier , OR { identifier } (shorthand)
     */
    private fun isObjectLiteralStart(): Boolean {
        if (!check(TokenType.LEFT_BRACE)) return false

        // Look ahead: { identifier :
        val saved = pos
        advance() // consume {

        // Empty object {}
        if (check(TokenType.RIGHT_BRACE)) {
            pos = saved
            return true
        }

        // Check for identifier or string followed by colon (regular property)
        val nextType = tokens.getOrNull(pos + 1)?.type
        val isObjLiteral =
            (check(TokenType.IDENTIFIER) || check(TokenType.STRING) || check(TokenType.BACKTICK_STRING)) &&
                    (nextType == TokenType.COLON ||
                            nextType == TokenType.COMMA ||
                            nextType == TokenType.RIGHT_BRACE)

        pos = saved
        return isObjLiteral
    }

    /**
     * Nullish coalescing (??)
     */
    private fun parseNullishCoalescing(): Expression {
        var expr = parseLogicalOr()

        while (match(TokenType.DOUBLE_QUESTION)) {
            val opToken = previous()
            val right = parseLogicalOr()
            expr = BinaryOperation(expr, BinaryOperator.NULLISH_COALESCE, right, opToken.toSourceLocation())
        }

        return expr
    }

    /**
     * Logical OR (||)
     */
    private fun parseLogicalOr(): Expression {
        var expr = parseLogicalAnd()

        while (match(TokenType.DOUBLE_PIPE)) {
            val opToken = previous()
            val right = parseLogicalAnd()
            expr = BinaryOperation(expr, BinaryOperator.OR, right, opToken.toSourceLocation())
        }

        return expr
    }

    /**
     * Logical AND (&&)
     */
    private fun parseLogicalAnd(): Expression {
        var expr = parseBitwiseOr()

        while (match(TokenType.DOUBLE_AMP)) {
            val opToken = previous()
            val right = parseBitwiseOr()
            expr = BinaryOperation(expr, BinaryOperator.AND, right, opToken.toSourceLocation())
        }

        return expr
    }

    /**
     * Bitwise OR (|)
     */
    private fun parseBitwiseOr(): Expression {
        var expr = parseBitwiseXor()

        while (match(TokenType.PIPE)) {
            val opToken = previous()
            val right = parseBitwiseXor()
            expr = BinaryOperation(expr, BinaryOperator.BITWISE_OR, right, opToken.toSourceLocation())
        }

        return expr
    }

    /**
     * Bitwise XOR (^)
     */
    private fun parseBitwiseXor(): Expression {
        var expr = parseBitwiseAnd()

        while (match(TokenType.CARET)) {
            val opToken = previous()
            val right = parseBitwiseAnd()
            expr = BinaryOperation(expr, BinaryOperator.BITWISE_XOR, right, opToken.toSourceLocation())
        }

        return expr
    }

    /**
     * Bitwise AND (&)
     */
    private fun parseBitwiseAnd(): Expression {
        var expr = parseEquality()

        while (match(TokenType.AMP)) {
            val opToken = previous()
            val right = parseEquality()
            expr = BinaryOperation(expr, BinaryOperator.BITWISE_AND, right, opToken.toSourceLocation())
        }

        return expr
    }

    /**
     * Equality operators: ===, !==, ==, !=
     */
    private fun parseEquality(): Expression {
        var expr = parseComparison()

        while (match(
                TokenType.TRIPLE_EQUALS, TokenType.NOT_DOUBLE_EQUALS,
                TokenType.DOUBLE_EQUALS, TokenType.NOT_EQUALS,
            )
        ) {
            val operator = when (previous().type) {
                TokenType.TRIPLE_EQUALS -> BinaryOperator.STRICT_EQUAL
                TokenType.NOT_DOUBLE_EQUALS -> BinaryOperator.STRICT_NOT_EQUAL
                TokenType.DOUBLE_EQUALS -> BinaryOperator.EQUAL
                TokenType.NOT_EQUALS -> BinaryOperator.NOT_EQUAL
                else -> error("Unexpected operator")
            }
            val opToken = previous()
            val right = parseComparison()
            expr = BinaryOperation(expr, operator, right, opToken.toSourceLocation())
        }

        return expr
    }

    /**
     * Relational/comparison operators: <, <=, >, >=, in
     */
    private fun parseComparison(): Expression {
        var expr = parseShift()

        while (match(
                TokenType.LESS_THAN, TokenType.LESS_EQUAL,
                TokenType.GREATER_THAN, TokenType.GREATER_EQUAL,
                TokenType.IN
            )
        ) {
            val operator = when (previous().type) {
                TokenType.LESS_THAN -> BinaryOperator.LESS_THAN
                TokenType.LESS_EQUAL -> BinaryOperator.LESS_THAN_OR_EQUAL
                TokenType.GREATER_THAN -> BinaryOperator.GREATER_THAN
                TokenType.GREATER_EQUAL -> BinaryOperator.GREATER_THAN_OR_EQUAL
                TokenType.IN -> BinaryOperator.IN
                else -> error("Unexpected operator")
            }
            val opToken = previous()
            val right = parseShift()
            expr = BinaryOperation(expr, operator, right, opToken.toSourceLocation())
        }

        return expr
    }

    /**
     * Shift operators: <<, >>, >>>
     */
    private fun parseShift(): Expression {
        var expr = parseAddition()

        while (match(TokenType.SHIFT_LEFT, TokenType.SHIFT_RIGHT, TokenType.UNSIGNED_SHIFT_RIGHT)) {
            val operator = when (previous().type) {
                TokenType.SHIFT_LEFT -> BinaryOperator.SHIFT_LEFT
                TokenType.SHIFT_RIGHT -> BinaryOperator.SHIFT_RIGHT
                TokenType.UNSIGNED_SHIFT_RIGHT -> BinaryOperator.UNSIGNED_SHIFT_RIGHT
                else -> error("Unexpected operator")
            }
            val opToken = previous()
            val right = parseAddition()
            expr = BinaryOperation(expr, operator, right, opToken.toSourceLocation())
        }

        return expr
    }

    /**
     * Addition and subtraction: +, -
     */
    private fun parseAddition(): Expression {
        var expr = parseMultiplication()

        while (match(TokenType.PLUS, TokenType.MINUS)) {
            val operator = if (previous().type == TokenType.PLUS) {
                BinaryOperator.ADD
            } else {
                BinaryOperator.SUBTRACT
            }
            val opToken = previous()
            val right = parseMultiplication()
            expr = BinaryOperation(expr, operator, right, opToken.toSourceLocation())
        }

        return expr
    }

    /**
     * Multiplication, division, and modulo: *, /, %
     */
    private fun parseMultiplication(): Expression {
        var expr = parsePower()

        while (match(TokenType.STAR, TokenType.SLASH, TokenType.PERCENT)) {
            val operator = when (previous().type) {
                TokenType.STAR -> BinaryOperator.MULTIPLY
                TokenType.SLASH -> BinaryOperator.DIVIDE
                TokenType.PERCENT -> BinaryOperator.MODULO
                else -> error("Unexpected operator")
            }
            val opToken = previous()
            val right = parsePower()
            expr = BinaryOperation(expr, operator, right, opToken.toSourceLocation())
        }

        return expr
    }

    /**
     * Exponentiation: a ** b  (right-associative, higher than multiplication)
     */
    private fun parsePower(): Expression {
        val base = parseUnary()

        if (match(TokenType.DOUBLE_STAR)) {
            val opToken = previous()
            val exponent = parsePower()  // Right-associative
            return BinaryOperation(base, BinaryOperator.POWER, exponent, opToken.toSourceLocation())
        }

        return base
    }

    /**
     * Unary operators: -expr, +expr, !expr, ++x, --x
     *
     * Note: ++ and -- are only treated as prefix increment/decrement when followed by an identifier.
     * A `-` or `--` directly before a number literal folds into a negative literal (see the first branches);
     * any other `++expr` / `--expr` is desugared to a double unary plus / minus.
     */
    private fun parseUnary(): Expression {
        when {
            // A minus directly before a number literal: `-42` is one literal. But `-6.db()` is REFUSED as
            // ambiguous. Kotlin and JS read it as -(6.db()), a musician reads it as (-6).db(), and the two
            // differ by a factor of four with no diagnostic. Making the literal win (tried 2026-09-08) made a
            // literal and a variable disagree (`-6.db()` versus `-g.db()` with `g = 6`), which the maintainer
            // called a design error on 2026-09-09. So KlangScript keeps Kotlin's precedence, `-x.db()` is
            // -(x.db()), and the one spelling where the reader cannot tell must carry parentheses.
            // A binary minus (`a -1`) never reaches this rule.
            check(TokenType.MINUS) && checkAt(1, TokenType.NUMBER) -> {
                val minus = advance()
                refuseAmbiguousNegativeReceiver(minus)
                return negativeLiteral(startLine = minus.line, startColumn = minus.column)
            }

            // `--42`: the second minus of `--` sits directly before the literal, same rule
            check(TokenType.MINUS_MINUS) && checkAt(1, TokenType.NUMBER) -> {
                val opToken = advance()
                refuseAmbiguousNegativeReceiver(opToken)
                val literal = negativeLiteral(startLine = opToken.line, startColumn = opToken.column + 1)
                return UnaryOperation(UnaryOperator.NEGATE, literal, opToken.toSourceLocation())
            }

            match(TokenType.MINUS, TokenType.PLUS, TokenType.EXCLAMATION, TokenType.TILDE) -> {
                val operator = when (previous().type) {
                    TokenType.MINUS -> UnaryOperator.NEGATE
                    TokenType.PLUS -> UnaryOperator.PLUS
                    TokenType.EXCLAMATION -> UnaryOperator.NOT
                    TokenType.TILDE -> UnaryOperator.BITWISE_NOT
                    else -> error("Unexpected unary operator")
                }
                val opToken = previous()
                val operand = parseUnary() // Right-associative
                return UnaryOperation(operator, operand, opToken.toSourceLocation())
            }

            // ++x — prefix increment when followed by an identifier
            check(TokenType.PLUS_PLUS) && tokens.getOrNull(pos + 1)?.type == TokenType.IDENTIFIER -> {
                advance() // consume ++
                val opToken = previous()
                val operand = parseCallExpression()
                return UnaryOperation(UnaryOperator.PREFIX_INCREMENT, operand, opToken.toSourceLocation())
            }

            // ++expr (non-identifier) — desugar as +(+expr) for compatibility
            check(TokenType.PLUS_PLUS) -> {
                advance() // consume ++
                val opToken = previous()
                val operand = parseUnary() // right-associative
                return UnaryOperation(
                    UnaryOperator.PLUS,
                    UnaryOperation(UnaryOperator.PLUS, operand, opToken.toSourceLocation()),
                    opToken.toSourceLocation()
                )
            }

            // --x — prefix decrement when followed by an identifier
            check(TokenType.MINUS_MINUS) && tokens.getOrNull(pos + 1)?.type == TokenType.IDENTIFIER -> {
                advance() // consume --
                val opToken = previous()
                val operand = parseCallExpression()
                return UnaryOperation(UnaryOperator.PREFIX_DECREMENT, operand, opToken.toSourceLocation())
            }

            // --expr (non-identifier) — desugar as -(-expr) for backward compatibility with --10
            check(TokenType.MINUS_MINUS) -> {
                advance() // consume --
                val opToken = previous()
                val operand = parseUnary() // right-associative
                return UnaryOperation(
                    UnaryOperator.NEGATE,
                    UnaryOperation(UnaryOperator.NEGATE, operand, opToken.toSourceLocation()),
                    opToken.toSourceLocation()
                )
            }
        }
        return parseCallExpression()
    }

    /**
     * Call and member access (method chaining)
     * CRITICAL: Allows alternating .prop, (), [ index ] in any order
     * Also handles postfix ++ and --
     * Fixes: lfo.shifted().range(0.1, 0.9), a no-argument call in the middle of a chain
     */
    private fun parseCallExpression(): Expression = parsePostfix(parsePrimary())

    /**
     * The cursor is on a `NUMBER` whose minus sign [minus] was just consumed. If a member access follows
     * the number, the spelling is ambiguous (`-6.db()`: the gain of -6 dB, or minus the gain of 6 dB?) and
     * is refused with both unambiguous spellings in the message. Anything else after the number is fine.
     */
    private fun refuseAmbiguousNegativeReceiver(minus: Token) {
        if (!checkAt(1, TokenType.DOT) && !checkAt(1, TokenType.QUESTION_DOT)) {
            return
        }

        val number = peek().text
        val member = tokens.getOrNull(pos + 2)?.takeIf { it.type == TokenType.IDENTIFIER }?.text ?: "method"
        val spelled = "-$number.$member(...)"

        throw KlangScriptSyntaxError(
            "'$spelled' is ambiguous: write '(-$number).$member(...)' to call $member on -$number, " +
                "or '-($number.$member(...))' to negate the result.",
            location = SourceLocation(currentSource, minus.line, minus.column, peek().endLine, peek().endColumn),
        )
    }

    /**
     * Consumes the `NUMBER` token at the cursor as a negative literal whose location starts at the minus sign
     * that precedes it (already consumed by the caller, alone or as the tail of `--`).
     */
    private fun negativeLiteral(startLine: Int, startColumn: Int): NumberLiteral {
        val number = advance()
        val location = SourceLocation(
            source = currentSource,
            startLine = startLine,
            startColumn = startColumn,
            endLine = number.endLine,
            endColumn = number.endColumn,
        )

        return NumberLiteral(-number.text.toDouble(), location)
    }

    /**
     * The postfix loop on an already parsed [start] expression: `.prop`, `?.prop`, `(args)`, `[index]`,
     * `x++`, `x--` in any order. [parseCallExpression] starts it on a primary; the negative-literal fold
     * in [parseUnary] starts it on the folded number.
     */
    private fun parsePostfix(start: Expression): Expression {
        var expr = start

        // Loop handles ANY sequence: .prop, (), [i], x++, x--, etc.
        while (true) {
            when {
                match(TokenType.DOT) -> {
                    val property = consume(TokenType.IDENTIFIER, "Expected property name after '.'")
                    expr = MemberAccess(expr, property.text, optional = false, property.toSourceLocation())
                }

                match(TokenType.QUESTION_DOT) -> {
                    val property = consume(TokenType.IDENTIFIER, "Expected property name after '?.'")
                    expr = MemberAccess(expr, property.text, optional = true, property.toSourceLocation())
                }

                match(TokenType.LEFT_PAREN) -> {
                    val lparen = previous()
                    val args = parseArguments()
                    consume(TokenType.RIGHT_PAREN, "Expected ')' after arguments")
                    expr = CallExpression(expr, args, lparen.toSourceLocation())
                }

                // Only treat [ as index access when preceded by an indexable expression
                // (identifier, call, member access, or another index access)
                // This prevents parsing `5\n[1, 2, 3]` as `5[1, 2, 3]`
                check(TokenType.LEFT_BRACKET) &&
                        (expr is Identifier || expr is CallExpression || expr is MemberAccess || expr is IndexAccess) -> {
                    advance()  // consume [
                    val lbracket = previous()
                    val index = parseExpression()
                    consume(TokenType.RIGHT_BRACKET, "Expected ']' after index")
                    expr = IndexAccess(expr, index, lbracket.toSourceLocation())
                }

                // Postfix ++ and -- only apply to assignable expressions (identifiers)
                // to avoid ambiguity with prefix ++/-- on the next line/statement
                check(TokenType.PLUS_PLUS) && expr is Identifier -> {
                    advance()  // consume ++
                    val opToken = previous()
                    expr = UnaryOperation(UnaryOperator.POSTFIX_INCREMENT, expr, opToken.toSourceLocation())
                }

                check(TokenType.MINUS_MINUS) && expr is Identifier -> {
                    advance()  // consume --
                    val opToken = previous()
                    expr = UnaryOperation(UnaryOperator.POSTFIX_DECREMENT, expr, opToken.toSourceLocation())
                }

                else -> break
            }
        }

        return expr
    }

    /**
     * Parse function call arguments. Handles trailing commas.
     *
     * Each argument is either:
     *   - Named: `IDENTIFIER = expr` — bound by parameter name
     *   - Positional: `expr`        — bound by index
     *
     * The call-site all-or-nothing rule (every argument must be the same kind)
     * is enforced at the interpreter / analyzer layer, not here, so a mixed
     * call still parses cleanly and produces a good error location later.
     */
    private fun parseArguments(): List<Argument> {
        val args = mutableListOf<Argument>()

        if (!check(TokenType.RIGHT_PAREN)) {
            do {
                args.add(parseArgument())
            } while (match(TokenType.COMMA) && !check(TokenType.RIGHT_PAREN))
        }

        return args
    }

    /**
     * Parse a single argument — named if it starts with `IDENTIFIER =`, else positional.
     *
     * Named detection uses 2-token lookahead so we don't confuse it with:
     *   - `==` / `===` comparisons    (EQ / STRICT_EQ tokens, not EQUALS)
     *   - `+=`, `-=`, ...             (compound-assign tokens, not EQUALS)
     *   - assignment-as-argument      (wrap in parens: `foo((x = 1))`)
     *
     * Only a bare `IDENTIFIER` followed by `EQUALS` at the start of an argument slot
     * is treated as a named argument; everything else falls through to
     * `parseExpression()` which handles assignment / ternary / arrow-fn / etc.
     */
    private fun parseArgument(): Argument {
        if (check(TokenType.IDENTIFIER) && checkAt(1, TokenType.EQUALS)) {
            val nameToken = advance()                    // consume IDENTIFIER
            advance()                                    // consume EQUALS
            val value = parseExpression()
            return Argument.Named(
                name = nameToken.text,
                value = value,
                nameLocation = nameToken.toSourceLocation(),
            )
        }
        return Argument.Positional(parseExpression())
    }

    /**
     * Primary expressions: literals, identifiers, grouping, objects, arrays
     */
    private fun parsePrimary(): Expression {
        return when {
            match(TokenType.NUMBER) -> {
                val token = previous()
                NumberLiteral(token.text.toDouble(), token.toSourceLocation())
            }

            match(TokenType.STRING) -> {
                val token = previous()
                StringLiteral(token.text, token.toSourceLocation())
            }

            match(TokenType.BACKTICK_STRING) -> {
                val token = previous()
                parseTemplateLiteralFromRaw(token.text, token.toSourceLocation())
            }

            match(TokenType.TRUE) -> {
                BooleanLiteral(true, previous().toSourceLocation())
            }

            match(TokenType.FALSE) -> {
                BooleanLiteral(false, previous().toSourceLocation())
            }

            match(TokenType.NULL) -> {
                NullLiteral
            }

            match(TokenType.IF) -> {
                parseIfExpression()
            }

            match(TokenType.IDENTIFIER) -> {
                val token = previous()
                Identifier(token.text, token.toSourceLocation())
            }

            match(TokenType.LEFT_BRACE) -> {
                parseObjectLiteral()
            }

            match(TokenType.LEFT_BRACKET) -> {
                parseArrayLiteral()
            }

            match(TokenType.LEFT_PAREN) -> {
                val expr = parseExpression()
                consume(TokenType.RIGHT_PAREN, "Expected ')' after expression")
                expr
            }

            else -> error("Expected expression")
        }
    }

    /**
     * Parse object literal: { key: value, ... }
     * Supports shorthand properties: { name } == { name: name }
     * Supports trailing commas
     * Mixed shorthand and regular: { name, age: 30 }
     */
    private fun parseObjectLiteral(): ObjectLiteral {
        val startToken = previous() // LEFT_BRACE consumed
        val properties = mutableListOf<Pair<String, Expression>>()

        while (!check(TokenType.RIGHT_BRACE) && !isAtEnd()) {
            // Key: identifier or string
            if (!match(TokenType.IDENTIFIER, TokenType.STRING, TokenType.BACKTICK_STRING)) {
                error("Expected property key")
            }
            val keyToken = previous()
            val key = keyToken.text

            val value = if (check(TokenType.COLON)) {
                // Regular property: key: value
                advance() // consume ':'
                parseExpression()
            } else {
                // Shorthand property: key  (same as key: key)
                // Only valid for IDENTIFIER keys (shorthand means "use variable with same name")
                if (keyToken.type != TokenType.IDENTIFIER) {
                    error("Shorthand property notation requires an identifier key")
                }
                Identifier(key, keyToken.toSourceLocation())
            }

            properties.add(key to value)

            if (!match(TokenType.COMMA)) break
            // Allow trailing comma
            if (check(TokenType.RIGHT_BRACE)) break
        }

        consume(TokenType.RIGHT_BRACE, "Expected '}'")
        return ObjectLiteral(properties, startToken.toSourceLocation())
    }

    /**
     * Parse array literal: [expr1, expr2, ...]
     * Supports trailing commas
     */
    private fun parseArrayLiteral(): ArrayLiteral {
        val startToken = previous() // LEFT_BRACKET consumed
        val elements = mutableListOf<Expression>()

        while (!check(TokenType.RIGHT_BRACKET) && !isAtEnd()) {
            elements.add(parseExpression())
            if (!match(TokenType.COMMA)) break
            // Allow trailing comma
            if (check(TokenType.RIGHT_BRACKET)) break
        }

        consume(TokenType.RIGHT_BRACKET, "Expected ']'")
        return ArrayLiteral(elements, startToken.toSourceLocation())
    }

    // ============================================================
    // Statement Parsing
    // ============================================================

    private fun parseStatement(): Statement {
        return when {
            match(TokenType.IMPORT) -> parseImportStatement()
            match(TokenType.EXPORT) -> {
                if (check(TokenType.LEFT_BRACE)) {
                    parseExportStatement()
                } else {
                    parseExportDeclaration()
                }
            }
            match(TokenType.LET) -> parseLetDeclaration()
            match(TokenType.CONST) -> parseConstDeclaration()
            match(TokenType.RETURN) -> parseReturnStatement()
            match(TokenType.IF) -> ExpressionStatement(parseIfExpression())
            match(TokenType.WHILE) -> parseWhileStatement()
            match(TokenType.DO) -> parseDoWhileStatement()
            match(TokenType.FOR) -> parseForStatement()
            match(TokenType.BREAK) -> {
                val tok = previous()
                BreakStatement(tok.toSourceLocation())
            }

            match(TokenType.CONTINUE) -> {
                val tok = previous()
                ContinueStatement(tok.toSourceLocation())
            }
            else -> parseExpression().let { ExpressionStatement(it, it.location) }
        }
    }

    /**
     * Parse let declaration: let x = expr OR let x
     */
    private fun parseLetDeclaration(): LetDeclaration {
        val letToken = previous() // LET consumed
        val name = consume(TokenType.IDENTIFIER, "Expected variable name")

        val initializer = if (match(TokenType.EQUALS)) {
            parseExpression()
        } else {
            null
        }

        return LetDeclaration(name.text, initializer, letToken.toSourceLocation())
    }

    /**
     * Parse const declaration: const x = expr
     */
    private fun parseConstDeclaration(): ConstDeclaration {
        val constToken = previous() // CONST consumed
        val name = consume(TokenType.IDENTIFIER, "Expected constant name")
        consume(TokenType.EQUALS, "Expected '=' after const name")
        val initializer = parseExpression()

        return ConstDeclaration(name.text, initializer, constToken.toSourceLocation())
    }

    /**
     * Parse return statement: return expr OR return
     */
    private fun parseReturnStatement(): ReturnStatement {
        val returnToken = previous() // RETURN consumed

        val value = if (isAtEnd() || check(TokenType.RIGHT_BRACE)) {
            null
        } else {
            parseExpression()
        }

        return ReturnStatement(value, returnToken.toSourceLocation())
    }

    /**
     * Parse import statement
     * Forms:
     * - import * from "lib"
     * - import * as name from "lib"
     * - import { x, y as z } from "lib"
     */
    private fun parseImportStatement(): ImportStatement {
        val importToken = previous() // IMPORT consumed

        when {
            match(TokenType.STAR) -> {
                // Wildcard or namespace import
                val namespaceAlias = if (matchAs()) {
                    consume(TokenType.IDENTIFIER, "Expected namespace name").text
                } else {
                    null
                }

                consumeFrom("Expected 'from' after import")
                val libraryName = consume(TokenType.STRING, "Expected library name").text

                return ImportStatement(
                    libraryName,
                    imports = null,
                    namespaceAlias = namespaceAlias,
                    location = importToken.toSourceLocation()
                )
            }

            match(TokenType.LEFT_BRACE) -> {
                // Selective import
                val imports = mutableListOf<Pair<String, String>>()

                do {
                    val name = consume(TokenType.IDENTIFIER, "Expected import name").text
                    val alias = if (matchAs()) {
                        consume(TokenType.IDENTIFIER, "Expected alias").text
                    } else {
                        name
                    }
                    imports.add(name to alias)
                } while (match(TokenType.COMMA))

                consume(TokenType.RIGHT_BRACE, "Expected '}'")
                consumeFrom("Expected 'from'")
                val libraryName = consume(TokenType.STRING, "Expected library name").text

                return ImportStatement(
                    libraryName,
                    imports,
                    location = importToken.toSourceLocation()
                )
            }

            else -> error("Expected '*' or '{' after 'import'")
        }
    }

    /**
     * Parse export statement: export { x, y as z }
     */
    private fun parseExportStatement(): ExportStatement {
        val exportToken = previous() // EXPORT consumed
        consume(TokenType.LEFT_BRACE, "Expected '{' after 'export'")

        val exports = mutableListOf<Pair<String, String>>()

        do {
            val localName = consume(TokenType.IDENTIFIER, "Expected export name").text
            val exportedName = if (matchAs()) {
                consume(TokenType.IDENTIFIER, "Expected exported name").text
            } else {
                localName
            }
            exports.add(localName to exportedName)
        } while (match(TokenType.COMMA))

        consume(TokenType.RIGHT_BRACE, "Expected '}'")

        return ExportStatement(exports, exportToken.toSourceLocation())
    }

    /**
     * Parse export declaration: export name = expr
     *
     * Combined immutable binding + export marker. The bound name is `const`-like
     * (cannot be reassigned) and is exported under its own name.
     */
    private fun parseExportDeclaration(): ExportDeclaration {
        val exportToken = previous() // EXPORT consumed
        val name = consume(TokenType.IDENTIFIER, "Expected name after 'export'")
        consume(TokenType.EQUALS, "Expected '=' after export name")
        val initializer = parseExpression()

        return ExportDeclaration(name.text, initializer, exportToken.toSourceLocation())
    }

    /**
     * Parse program: zero or more statements
     */
    private fun parseProgram(): Program {
        val statements = mutableListOf<Statement>()
        // Nothing precedes the first statement, so it never needs a separator
        var separated = true

        while (!isAtEnd()) {
            // Only reaches anything on the first iteration (a program may open with `;`); afterwards the
            // trailing skip below has already eaten every semicolon, which is what sets `separated`.
            skipSemicolons()

            if (isAtEnd()) break

            if (!separated) {
                requireStatementBoundary()
            }

            statements.add(parseStatement())
            separated = skipSemicolons()
        }

        return Program(statements)
    }

    /**
     * Parse if expression: if ( condition ) { then } [ else { else } | else if ... ]
     *
     * `if` is an expression so it can produce a value.
     * At statement level it becomes an ExpressionStatement(IfExpression(...)).
     */
    private fun parseIfExpression(): IfExpression {
        val ifToken = previous() // IF consumed
        consume(TokenType.LEFT_PAREN, "Expected '(' after 'if'")
        val condition = parseExpression()
        consume(TokenType.RIGHT_PAREN, "Expected ')' after if condition")
        consume(TokenType.LEFT_BRACE, "Expected '{' after if condition")
        val thenBranch = parseBlockStatements()
        consume(TokenType.RIGHT_BRACE, "Expected '}' after if body")

        val elseBranch = if (match(TokenType.ELSE)) {
            if (match(TokenType.IF)) {
                // else if chain
                ElseBranch.If(parseIfExpression())
            } else {
                // else block
                consume(TokenType.LEFT_BRACE, "Expected '{' after 'else'")
                val elseStmts = parseBlockStatements()
                consume(TokenType.RIGHT_BRACE, "Expected '}' after else body")
                ElseBranch.Block(elseStmts)
            }
        } else {
            null
        }

        return IfExpression(condition, thenBranch, elseBranch, ifToken.toSourceLocation())
    }

    /**
     * Parse statements inside a block (until RIGHT_BRACE or EOF)
     * Skips semicolons between statements.
     */
    private fun parseBlockStatements(): List<Statement> {
        val statements = mutableListOf<Statement>()
        // The `{` precedes the first statement, so it never needs a separator
        var separated = true

        while (!check(TokenType.RIGHT_BRACE) && !isAtEnd()) {
            // See parseProgram: only the first iteration can find anything here (`{ ; f() }`)
            skipSemicolons()

            if (check(TokenType.RIGHT_BRACE) || isAtEnd()) break

            if (!separated) {
                requireStatementBoundary()
            }

            statements.add(parseStatement())
            separated = skipSemicolons()
        }

        return statements
    }

    /**
     * Parse template literal from raw backtick string content.
     *
     * Scans the raw string for `${...}` patterns and builds a TemplateLiteral node.
     * If no interpolations exist, returns a plain StringLiteral for efficiency.
     */
    private fun parseTemplateLiteralFromRaw(raw: String, location: SourceLocation): Expression {
        // Quick check: if no '${' in the string, return a plain StringLiteral
        if (!raw.contains("\${")) {
            return StringLiteral(raw, location)
        }

        val parts = mutableListOf<TemplatePart>()
        var idx = 0

        while (idx < raw.length) {
            // Find next `${`
            val interpStart = raw.indexOf("\${", idx)
            if (interpStart < 0) {
                // Rest is plain text
                if (idx < raw.length) {
                    parts.add(TemplatePart.Text(raw.substring(idx)))
                }
                break
            }

            // Add text before `${`
            if (interpStart > idx) {
                parts.add(TemplatePart.Text(raw.substring(idx, interpStart)))
            }

            // Find matching `}`, tracking string literals so braces inside strings are ignored.
            val exprStart = interpStart + 2
            var depth = 1
            var j = exprStart
            var inString: Char? = null   // current string delimiter (' or "), null if not in string
            var escaped = false          // true if previous char was backslash inside a string
            while (j < raw.length && depth > 0) {
                val c = raw[j]
                when {
                    escaped -> escaped = false
                    inString != null && c == '\\' -> escaped = true
                    inString != null && c == inString -> inString = null
                    inString == null && (c == '\'' || c == '"') -> inString = c
                    inString == null && c == '{' -> depth++
                    inString == null && c == '}' -> depth--
                }
                if (depth > 0) j++
            }
            if (depth != 0) {
                error("Unterminated template literal interpolation")
            }
            val exprSource = raw.substring(exprStart, j)

            // Parse the expression inside ${...} using a sub-parser
            val subProgram = parse(exprSource, currentSource)
            if (subProgram.statements.isEmpty()) {
                error("Empty expression in template literal interpolation")
            }

            val expr = when (val stmt = subProgram.statements.first()) {
                is ExpressionStatement -> stmt.expression
                else -> error("Template literal interpolation must be an expression")
            }
            parts.add(TemplatePart.Interp(expr))

            idx = j + 1 // Skip past the closing `}`
        }

        // If only one text part, return StringLiteral
        if (parts.size == 1 && parts[0] is TemplatePart.Text) {
            return StringLiteral((parts[0] as TemplatePart.Text).value, location)
        }

        return TemplateLiteral(parts, location)
    }

    /**
     * Parse while statement: while ( condition ) { body }
     */
    private fun parseWhileStatement(): WhileStatement {
        val whileToken = previous() // WHILE consumed
        consume(TokenType.LEFT_PAREN, "Expected '(' after 'while'")

        val condition = parseExpression()
        consume(TokenType.RIGHT_PAREN, "Expected ')' after while condition")
        consume(TokenType.LEFT_BRACE, "Expected '{' after while condition")

        val body = parseBlockStatements()
        consume(TokenType.RIGHT_BRACE, "Expected '}' after while body")

        return WhileStatement(condition, body, whileToken.toSourceLocation())
    }

    /**
     * Parse do-while statement: do { body } while ( condition )
     */
    private fun parseDoWhileStatement(): DoWhileStatement {
        val doToken = previous() // DO consumed
        consume(TokenType.LEFT_BRACE, "Expected '{' after 'do'")

        val body = parseBlockStatements()
        consume(TokenType.RIGHT_BRACE, "Expected '}' after do body")
        consume(TokenType.WHILE, "Expected 'while' after do body")
        consume(TokenType.LEFT_PAREN, "Expected '(' after 'while'")

        val condition = parseExpression()
        consume(TokenType.RIGHT_PAREN, "Expected ')' after do-while condition")

        return DoWhileStatement(body, condition, doToken.toSourceLocation())
    }

    /**
     * Parse for statement: for ( [ init ] ; [ condition ] ; [ update ] ) { body }
     *
     * The for loop runs in its own scope (init let declarations are scoped to loop).
     * Semicolons are used as separators in the for header (not statement terminators).
     * The init can be a let declaration, an assignment expression, or empty.
     */
    private fun parseForStatement(): ForStatement {
        val forToken = previous() // FOR consumed
        consume(TokenType.LEFT_PAREN, "Expected '(' after 'for'")

        // Parse init (optional)
        val init: Statement? = when {
            check(TokenType.SEMICOLON) -> null  // empty init
            match(TokenType.LET) -> parseLetDeclaration()
            match(TokenType.CONST) -> parseConstDeclaration()
            else -> {
                val expr = parseExpression()
                ExpressionStatement(expr)
            }
        }
        consume(TokenType.SEMICOLON, "Expected ';' after for init")

        // Parse condition (optional)
        val condition: Expression? = if (check(TokenType.SEMICOLON)) null else parseExpression()
        consume(TokenType.SEMICOLON, "Expected ';' after for condition")

        // Parse update (optional)
        val update: Expression? = if (check(TokenType.RIGHT_PAREN)) null else parseExpression()
        consume(TokenType.RIGHT_PAREN, "Expected ')' after for clauses")

        consume(TokenType.LEFT_BRACE, "Expected '{' after for header")
        val body = parseBlockStatements()
        consume(TokenType.RIGHT_BRACE, "Expected '}' after for body")

        return ForStatement(init, condition, update, body, forToken.toSourceLocation())
    }
}
